class MockupConfigurator extends HTMLElement {
  connectedCallback() {
    this.stack = this.querySelector('[data-stack]');
    this.mockup = this.querySelector('[data-mockup]');
    this.frameLayer = this.querySelector('[data-layer="frame"]');
    this.borderLayer = this.querySelector('[data-layer="border"]');
    this.artworkWindow = this.querySelector('.mockup-configurator__artwork-window');
    this.artworkImg = this.querySelector('.mockup-configurator__artwork');
    this.borderGroup = this.querySelector('[data-border-group]');
    this.prevArrow = this.querySelector('[data-arrow="prev"]');
    this.nextArrow = this.querySelector('[data-arrow="next"]');
    this.priceEl = this.querySelector('[data-price]');
    this.addToCartBtn = this.querySelector('[data-add-to-cart]');
    this.statusEl = this.querySelector('[data-cart-status]');

    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.slideIndex = 0;

    this.printVariants = JSON.parse(this.querySelector('[data-print-variants]').textContent);

    this.moneyFormatter = new Intl.NumberFormat(this.dataset.locale || undefined, {
      style: 'currency',
      currency: this.dataset.currency,
    });

    this.addEventListener('change', this.handleChange.bind(this));
    this.prevArrow?.addEventListener('click', () => this.stepSlide(-1));
    this.nextArrow?.addEventListener('click', () => this.stepSlide(1));
    this.addToCartBtn?.addEventListener('click', () => this.addToCart());

    this.artworkImg.addEventListener('load', () => this.updateArtworkWindowSize());
    if (this.artworkImg.complete) this.updateArtworkWindowSize();
    new ResizeObserver(() => this.updateArtworkWindowSize()).observe(this.stack);

    this.syncPrintType(this.querySelector('input[data-print-type]:checked')?.value);
    this.updatePrice();
    // Show the print at 100% on load regardless of which size is pre-checked;
    // the real size scale only kicks in once the shopper picks a size themselves.
    this.mockup.style.transform = 'scale(1)';
  }

  getSizeScale() {
    const scaleBySizeCode = { A4: 0.55, A3: 0.7, A2: 0.85, A1: 1 };
    return scaleBySizeCode[this.getSizeCode()] ?? 1;
  }

  updateSizeScale() {
    this.mockup.style.transform = `scale(${this.getSizeScale()})`;
  }

  getWindowScale() {
    if (this.stack.classList.contains('has-border')) return 0.537;
    if (this.stack.classList.contains('has-frame')) return 0.758;
    return 0.8;
  }

  updateArtworkWindowSize() {
    const { naturalWidth, naturalHeight } = this.artworkImg;
    const maxDim = this.stack.getBoundingClientRect().width * this.getWindowScale();

    let width = maxDim;
    let height = maxDim;
    if (naturalWidth && naturalHeight) {
      const ratio = naturalWidth / naturalHeight;
      if (ratio >= 1) {
        height = maxDim / ratio;
      } else {
        width = maxDim * ratio;
      }
      this.stack.classList.toggle('is-landscape', naturalWidth > naturalHeight);
    }

    this.artworkWindow.style.width = `${width}px`;
    this.artworkWindow.style.height = `${height}px`;
  }

  handleChange(event) {
    const input = event.target;
    if (input.matches('[data-size-option]')) {
      this.updateSizeScale();
    } else if (input.matches('[data-print-type]')) {
      this.syncPrintType(input.value);
    } else if (input.matches('[data-frame-option]')) {
      if (input.value === 'none') {
        this.clearFrame();
      } else {
        this.setFrame(input);
      }
    } else if (input.matches('[data-border-option]')) {
      this.setBorder(input);
    }
    this.updatePrice();
  }

  syncPrintType(printType) {
    const isCanvas = printType === 'canvas';

    this.querySelectorAll('[data-frame-option]').forEach((input) => {
      const matchesScope = input.dataset.scope === printType;
      input.closest('.mockup-configurator__pill').hidden = !matchesScope;
      if (!matchesScope && input.checked) input.checked = false;
    });

    if (isCanvas) {
      this.borderGroup.hidden = true;
      this.clearBorder();
    } else {
      this.borderGroup.hidden = false;
    }

    this.clearFrame();
  }

  setFrame(input) {
    this.frameLayer.hidden = false;
    this.stack.classList.add('has-frame');
    this.updateFrameSrc();

    const isCanvas = input.dataset.scope === 'canvas';
    this.querySelectorAll('[data-border-option]').forEach((borderInput) => {
      borderInput.disabled = isCanvas;
    });
    this.updateArtworkWindowSize();
  }

  updateFrameSrc() {
    const frameInput = this.getFrameInput();
    if (!frameInput) return;
    const hasBorder = !!this.getBorderInput();
    this.frameLayer.src = (!hasBorder && frameInput.dataset.assetOnly)
      ? frameInput.dataset.assetOnly
      : frameInput.dataset.asset;
  }

  clearFrame() {
    this.frameLayer.hidden = true;
    this.frameLayer.removeAttribute('src');
    this.stack.classList.remove('has-frame');
    this.querySelectorAll('[data-border-option]').forEach((borderInput) => {
      borderInput.disabled = true;
    });
    this.clearBorder();

    const printType = this.getPrintType();
    const noneInput = this.querySelector(`[data-frame-option][value="none"][data-scope="${printType}"]`);
    if (noneInput) noneInput.checked = true;
    this.updateArtworkWindowSize();
  }

  setBorder(input) {
    if (input.value === 'none') {
      this.clearBorder();
      return;
    }
    this.borderLayer.src = input.dataset.asset;
    this.borderLayer.hidden = false;
    this.stack.classList.add('has-border');
    this.updateFrameSrc();
    this.updateArtworkWindowSize();
  }

  clearBorder() {
    this.borderLayer.hidden = true;
    this.borderLayer.removeAttribute('src');
    this.stack.classList.remove('has-border');
    const noneInput = this.querySelector('[data-border-option][value="none"]');
    if (noneInput) noneInput.checked = true;
    this.updateFrameSrc();
    this.updateArtworkWindowSize();
  }

  stepSlide(direction) {
    if (this.slides.length <= 1) return;
    this.slides[this.slideIndex].hidden = true;
    this.slideIndex = (this.slideIndex + direction + this.slides.length) % this.slides.length;

    const enteringConfigurator = this.slideIndex === 0;
    if (enteringConfigurator) {
      // Returning to the configurator slide re-triggers the ResizeObserver
      // (the stack goes from 0 size while hidden back to its real size),
      // which recomputes window size/scale -- suppress transitions for
      // that one recompute so it doesn't visibly animate on slide swap.
      this.mockup.style.transition = 'none';
      this.artworkWindow.style.transition = 'none';
    }

    this.slides[this.slideIndex].hidden = false;

    if (enteringConfigurator) {
      setTimeout(() => {
        this.mockup.style.transition = '';
        this.artworkWindow.style.transition = '';
      }, 50);
    }
  }

  getSizeCode() {
    return this.querySelector('[data-size-option]:checked')?.dataset.sizeCode;
  }

  getPrintType() {
    return this.querySelector('[data-print-type]:checked')?.value;
  }

  getFrameInput() {
    const input = this.querySelector('[data-frame-option]:checked');
    return input && input.value !== 'none' ? input : null;
  }

  getBorderInput() {
    const input = this.querySelector('[data-border-option]:checked');
    return input && input.value !== 'none' ? input : null;
  }

  getFramingTier() {
    if (this.getBorderInput()) return 'Frame + Border';
    if (this.getFrameInput()) return 'Frame Only';
    return 'No Frame';
  }

  resolvePrintVariant() {
    const sizeCode = this.getSizeCode();
    const printType = this.getPrintType();
    if (!sizeCode || !printType) return null;
    return this.printVariants[`${sizeCode}|${printType}|${this.getFramingTier()}`] || null;
  }

  updatePrice() {
    const printVariant = this.resolvePrintVariant();
    this.priceEl.textContent = this.moneyFormatter.format((printVariant?.price || 0) / 100);
    if (this.addToCartBtn) this.addToCartBtn.disabled = !printVariant;
  }

  async addToCart() {
    const printVariant = this.resolvePrintVariant();
    if (!printVariant) return;

    const frameInput = this.getFrameInput();
    const borderInput = this.getBorderInput();
    const properties = {};
    if (frameInput) properties['Frame Colour'] = frameInput.dataset.color;
    if (borderInput) properties['Mount Colour'] = borderInput.dataset.color;

    // Match the theme's own product-form: request the cart-drawer/cart-icon
    // sections and hand the response straight to the cart element's own
    // render method, so the drawer/icon update immediately instead of only
    // on the next page load.
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const body = { items: [{ id: printVariant.id, quantity: 1, properties }] };
    if (cart) {
      body.sections = cart.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname;
    }

    this.addToCartBtn.disabled = true;
    this.setStatus('Adding…');

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result?.description || 'Could not add to cart');
      }

      if (cart) {
        cart.renderContents(result);
        cart.classList.remove('is-empty');
        // The sections embedded in this response can occasionally reflect a
        // stale (pre-add) cart state in the drawer specifically, even though
        // the cart itself is already correct -- a separate, fresh section
        // fetch (same thing a manual page refresh does) reliably fixes it.
        this.refreshCartSections(cart);
      }
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'mockup-configurator', productVariantId: printVariant.id });
      }

      this.setStatus('Added to cart.');
    } catch (err) {
      this.setStatus(err.message || 'Something went wrong.');
    } finally {
      this.addToCartBtn.disabled = false;
    }
  }

  async refreshCartSections(cart) {
    try {
      const sectionIds = cart.getSectionsToRender().map((section) => section.id);
      const response = await fetch(`${window.location.pathname}?sections=${sectionIds.join(',')}`);
      const sections = await response.json();
      cart.renderContents({ sections });
      cart.classList.remove('is-empty');
    } catch (err) {
      console.error('Could not refresh cart drawer contents:', err);
    }
  }

  setStatus(message) {
    if (!this.statusEl) return;
    this.statusEl.textContent = message;
    this.statusEl.hidden = !message;
  }
}

customElements.define('mockup-configurator', MockupConfigurator);
